import { Suspense, useState } from 'react'
import { Button, Group, LoadingOverlay, Stack, Text, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { useTranslation } from 'react-i18next'
import { TbCameraPlus, TbDatabasePlus, TbDatabaseShare, TbTrash } from 'react-icons/tb'
import PageShell from '@/components/Common/PageShell'
import type { ControlDataSchema, ShortenedSnapshotsWithOverrides, SyncResponse } from '@/api/main/types.gen'
import { GenericTable } from '@/components/Table/GenericTable'
import { useGenericTable } from '@/components/Table/useGenericTable'
import { useFilter } from '@/components/Table/useFilter'
import { CreateSchemaModal } from './CreateSchemaModal'
import { CreateSnapshotModal } from './CreateSnapshotModal'
import { SyncSnapshotModal } from './SyncSnapshotModal'
import { useMapSnapshots } from './useMapSnapshots'

const SnapshotsLoading = () => (
  <PageShell title="Map snapshots">
    <LoadingOverlay visible />
  </PageShell>
)

export default function MapSnapshotsPage() {
  return (
    <Suspense fallback={<SnapshotsLoading />}>
      <MapSnapshotsPageInner />
    </Suspense>
  )
}

function MapSnapshotsPageInner() {
  const { t } = useTranslation('mapSnapshots')
  const { snapshots, schemas, createSnapshot, syncSnapshot, deleteSnapshot } = useMapSnapshots()

  const [createOpened, createCtl] = useDisclosure(false)
  const [syncOpened, syncCtl] = useDisclosure(false)
  const [createSchemaOpened, createSchemaCtl] = useDisclosure(false)
  const [syncTarget, setSyncTarget] = useState<ShortenedSnapshotsWithOverrides | null>(null)

  const onSync = (snapshot: ShortenedSnapshotsWithOverrides) => {
    setSyncTarget(snapshot)
    syncCtl.open()
  }

  const confirmDelete = (snapshot: ShortenedSnapshotsWithOverrides) => {
    modals.openConfirmModal({
      title: t('confirmDeleteTitle'),
      children: <Text size="sm">{t('confirmDeleteBody', { name: snapshot.snapshot_name })}</Text>,
      labels: { confirm: t('delete'), cancel: t('cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteSnapshot.mutate({ body: { snapshot_name: snapshot.snapshot_name } }),
    })
  }

  const handleSync = async (snapshotName: string, destination: string): Promise<SyncResponse[] | undefined> => {
    return new Promise(resolve => {
      syncSnapshot.mutate(
        { body: { snapshot_from: snapshotName, destination_schema: destination } },
        {
          onSuccess: data => resolve(data ?? undefined),
          onError: () => resolve(undefined),
        }
      )
    })
  }

  return (
    <PageShell
      title={t('mapSnapshots', { ns: 'nav' })}
      action={
        <Group gap="xs">
          <Button leftSection={<TbDatabasePlus size={16} />} onClick={createSchemaCtl.open} size="xs" variant="light">
            {t('createSchema')}
          </Button>
          <Button leftSection={<TbCameraPlus size={16} />} onClick={createCtl.open} size="xs">
            {t('createSnapshot')}
          </Button>
        </Group>
      }
    >
      <Stack gap="xl">
        <Stack gap="xs">
          <Title order={4} c="light-dark(#868e96, #c9c9c9)">
            {t('existingSnapshots')}
          </Title>
          <SnapshotsTable
            snapshots={snapshots}
            onSync={onSync}
            onDelete={confirmDelete}
            isDeleting={deleteSnapshot.isPending}
          />
        </Stack>

        <Stack gap="xs">
          <Title order={4} c="light-dark(#868e96, #c9c9c9)">
            {t('controlSchemas')}
          </Title>
          <SchemasTable schemas={schemas} />
        </Stack>
      </Stack>

      <CreateSnapshotModal
        opened={createOpened}
        onClose={createCtl.close}
        snapshots={snapshots}
        isSubmitting={createSnapshot.isPending}
        onSubmit={values =>
          createSnapshot.mutate(
            { body: values },
            {
              onSuccess: () => createCtl.close(),
            }
          )
        }
      />

      <SyncSnapshotModal
        opened={syncOpened}
        onClose={() => {
          syncCtl.close()
          setSyncTarget(null)
        }}
        snapshot={syncTarget}
        schemas={schemas}
        isSubmitting={syncSnapshot.isPending}
        onSync={handleSync}
      />

      <CreateSchemaModal
        opened={createSchemaOpened}
        onClose={createSchemaCtl.close}
        snapshots={snapshots}
        existingSchemaNames={schemas.map(s => s.schema_name)}
        isSubmitting={syncSnapshot.isPending}
        onSubmit={async (snapshotName, schemaName) => {
          const result = await handleSync(snapshotName, schemaName)
          createSchemaCtl.close()
          return result
        }}
      />
    </PageShell>
  )
}

function SnapshotsTable({
  snapshots,
  onSync,
  onDelete,
  isDeleting,
}: {
  snapshots: ShortenedSnapshotsWithOverrides[]
  onSync: (s: ShortenedSnapshotsWithOverrides) => void
  onDelete: (s: ShortenedSnapshotsWithOverrides) => void
  isDeleting: boolean
}) {
  const { t } = useTranslation('mapSnapshots')

  type Row = ShortenedSnapshotsWithOverrides & { actions: string }
  const data: Row[] = snapshots.map(s => ({ ...s, actions: '' }))

  const { filteredData, handleFilterFieldChange, handleSearchChange, search, filterField } = useFilter({
    data,
  })

  const {
    currentPage,
    tableColumns,
    paginatedData,
    handlePageChange,
    setTableColumns,
    sortDirection,
    setSortDirection,
    sortField,
    setSortField,
  } = useGenericTable<Row>({
    defaultSortField: 'snapshot_name',
    defaultSortDirection: 'desc',
    tableKey: 'map-snapshots-list',
    filteredData,
    columns: [
      {
        key: 'snapshot_name',
        label: t('snapshotName'),
        visible: true,
        sortable: true,
        render: row => (
          <Text ff="monospace" size="sm">
            {row.snapshot_name}
          </Text>
        ),
      },
      {
        key: 'overrides_hash',
        label: t('overridesHash'),
        visible: true,
        sortable: false,
        render: row => (
          <Text ff="monospace" size="xs" c="dimmed" truncate>
            {row.overrides_hash}
          </Text>
        ),
      },
      {
        key: 'actions',
        label: t('actions'),
        visible: true,
        sortable: false,
        align: 'right',
        render: row => (
          <Group gap="xs" justify="flex-end">
            <Button size="xs" variant="light" leftSection={<TbDatabaseShare size={14} />} onClick={() => onSync(row)}>
              {t('sync')}
            </Button>
            <Button
              size="xs"
              color="red"
              variant="light"
              leftSection={<TbTrash size={14} />}
              onClick={() => onDelete(row)}
              loading={isDeleting}
            >
              {t('delete')}
            </Button>
          </Group>
        ),
      },
    ],
  })

  return (
    <GenericTable
      data={data}
      columns={tableColumns}
      onColumnsChange={setTableColumns}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      filterField={filterField}
      onFilterFieldChange={handleFilterFieldChange}
      onSearchChange={handleSearchChange}
      getRowKey={row => row.snapshot_name}
      emptyMessage={t('noSnapshots')}
      paginatedData={paginatedData}
      filteredData={filteredData}
      search={search}
      itemsPerPage={20}
      csvFileName="map-snapshots.csv"
      sortDirection={sortDirection}
      onSortDirectionChange={setSortDirection}
      sortField={sortField}
      onSortFieldChange={setSortField}
    />
  )
}

function SchemasTable({ schemas }: { schemas: ControlDataSchema[] }) {
  const { t } = useTranslation('mapSnapshots')

  const { filteredData, handleFilterFieldChange, handleSearchChange, search, filterField } = useFilter({
    data: schemas,
  })

  const {
    currentPage,
    tableColumns,
    paginatedData,
    handlePageChange,
    setTableColumns,
    sortDirection,
    setSortDirection,
    sortField,
    setSortField,
  } = useGenericTable<ControlDataSchema>({
    defaultSortField: 'schema_name',
    defaultSortDirection: 'asc',
    tableKey: 'control-data-schemas',
    filteredData,
    columns: [
      {
        key: 'schema_name',
        label: t('schemaName'),
        visible: true,
        sortable: true,
        render: row => (
          <Text ff="monospace" size="sm">
            {row.schema_name}
          </Text>
        ),
      },
      {
        key: 'overrides_version',
        label: t('overridesVersion'),
        visible: true,
        sortable: true,
      },
      {
        key: 'overrides_hash',
        label: t('overridesHash'),
        visible: true,
        sortable: false,
        render: row => (
          <Text ff="monospace" size="xs" c="dimmed" truncate>
            {row.overrides_hash}
          </Text>
        ),
      },
    ],
  })

  return (
    <GenericTable
      data={schemas}
      columns={tableColumns}
      onColumnsChange={setTableColumns}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      filterField={filterField}
      onFilterFieldChange={handleFilterFieldChange}
      onSearchChange={handleSearchChange}
      getRowKey={row => row.schema_name}
      emptyMessage={t('noSchemas')}
      paginatedData={paginatedData}
      filteredData={filteredData}
      search={search}
      itemsPerPage={20}
      csvFileName="control-data-schemas.csv"
      sortDirection={sortDirection}
      onSortDirectionChange={setSortDirection}
      sortField={sortField}
      onSortFieldChange={setSortField}
    />
  )
}
